import { drupalSettings } from "../utils/config";
import ajax from "./ajax";
import { getCsrfTokenApi } from "./auth";

export const getRecipientListApi = ({ yatchId }) => {
  return ajax({
    method: "get",
    url: `/rest/beneficiary/list?yacht=${yatchId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getRecipientDetailsApi = ({ yatchId, recipientId }) => {
  return ajax({
    method: "get",
    url: `/rest/beneficiary/list?yacht=${yatchId}&id=${recipientId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getCategoryListApi = ({ yatchId }) => {
  return ajax({
    method: "get",
    url: `/rest/beneficiary/options?yacht=${yatchId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getSubCategoryListApi = ({ yatchId, categoryId }) => {
  return ajax({
    method: "get",
    url: `/rest/beneficiary/options?yacht=${yatchId}&parent_term_id=${categoryId}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getEmbeddedInitApi = ({ yacht_id }) => {
  return ajax({
    method: "get",
    url: `/airwallex/embedded/details/yatch/${yacht_id}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const getSCAEmbeddedInitApi = ({ uid, yacht_id }) => {
  return ajax({
    method: "get",
    url: `/airwallex/embedded/sca/details/${uid}/yatch/${yacht_id}`,
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const submitTransferApi = async ({ formData }) => {
  try {
    // Step 1: Get CSRF token
    const token = await getCsrfTokenApi();

    // Step 2: Call API with token
    return ajax({
      method: "POST",
      url: "/rest/add/beneficiary",
      headers: {
        "X-CSRF-Token": token,
        "Content-Type": "multipart/form-data",
      },
      processData: false,
      contentType: false,
      data: formData, // FormData object
    });
  } catch (error) {
    console.error("Error submitting transfer:", error);
    throw error;
  }
};

/**
 * @param {object} payload - Request body (payload, recipient_name, uid).
 * @param {{ scaToken?: string }} options - Optional. Pass scaToken when retrying after SCA verification.
 */
export const addRecipientApi = async (payload, options = {}) => {
  try {
    const token = await getCsrfTokenApi();
    const headers = {
      "X-CSRF-Token": token,
      "Content-Type": "application/json",
    };
    const scaToken = options.scaToken != null ? String(options.scaToken).trim() : "";
    if (scaToken) {
      headers["x-sca-token"] = scaToken;
    }

    if (drupalSettings?.user?.sca_access) {
      payload.payload.sca_exemptible = true;
    }

    console.log("payload", payload);

    return ajax({
      method: "POST",
      url: "/rest/add/recipient",
      headers,
      data: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Error submitting add recipient:", error);
    throw error;
  }
};

/**
 * Edit existing recipient in Airwallex + sync local beneficiary entity.
 *
 * @param {object} payload - Request body (payload, recipient_name, uid, beneficiary_id).
 * @param {{ scaToken?: string }} options - Optional SCA token when retrying after 403.
 */
export const editRecipientApi = async (payload, options = {}) => {
  try {
    const token = await getCsrfTokenApi();
    const headers = {
      "X-CSRF-Token": token,
      "Content-Type": "application/json",
    };
    const scaToken = options.scaToken != null ? String(options.scaToken).trim() : "";
    if (scaToken) {
      headers["x-sca-token"] = scaToken;
    }

    if (drupalSettings?.user?.sca_access) {
      payload.payload.sca_exemptible = true;
    }

    return ajax({
      method: "POST",
      url: "/rest/add/recipient",
      headers,
      data: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Error submitting edit recipient:", error);
    throw error;
  }
};

export const scaLoginApi = async (payload) => {
  try {
    return ajax({
      method: "POST",
      url: "/rest/user/sca/login",
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify(payload),
    });
  } catch (error) {
    console.error("Error submitting transfer:", error);
    throw error;
  }
};

export const sendSMSApi = async ({ uid }) => {
  try {
    return ajax({
      method: "POST",
      url: "/rest/sms/send",
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({
        uid: uid,
      }),
      authMode: "oauth",
    });
  } catch (error) {
    console.error("Error sending SMS:", error);
    throw error;
  }
};

export const verifySMSApi = async ({ uid, sms_code }) => {
  try {
    return ajax({
      method: "POST",
      url: "/rest/sms/verification",
      headers: {
        "Content-Type": "application/json",
      },
      data: JSON.stringify({
        uid,
        sms_code,
      }),
      authMode: "oauth",
    });
  } catch (error) {
    console.error("Error verifying SMS code:", error);
    throw error;
  }
};

export const fetchUserInfoApi = async ({ uid }) => {
  try {
    return ajax({
      method: "GET",
      url: `/rest/user/info?uid=${uid}`,
      headers: {
        "Content-Type": "application/json",
      },
      authMode: "oauth",
    });
  } catch (error) {
    console.error("Error submitting transfer:", error);
    throw error;
  }
};

export const viewCardPinApi = async ({ card_id, connected_account_id, sca_token }) => {
  try {
    return ajax({
      method: "POST",
      url: "/rest/card/view-pin",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        card_id,
        connected_account_id,
        ...(sca_token ? { sca_token } : {}),
      },
      authMode: "oauth",
    });
  } catch (error) {
    console.error("Error fetching card PIN:", error);
    throw error;
  }
};

export const viewCardDetailsApi = async ({ card_id, connected_account_id, sca_token }) => {
  try {
    return ajax({
      method: "POST",
      url: "/rest/card/view-details",
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        card_id,
        connected_account_id,
        ...(sca_token ? { sca_token } : {}),
      },
      authMode: "oauth",
    });
  } catch (error) {
    console.error("Error fetching card details:", error);
    throw error;
  }
};

// Use session cookie (no OAuth) so requests work when user is on dashboard with Drupal login
export const bankTransferConfirmationApi = async ({ order_id }) => {
  return ajax({
    method: "POST",
    url: "/rest/bank-transfer/confirmation",
    headers: { "Content-Type": "application/json" },
    data: JSON.stringify({ order_id }),
    authMode: "oauth",
  });
};

export const bankTransferApproveApi = async (payload) => {
  return ajax({
    method: "POST",
    url: "/rest/bank-transfer/approve",
    headers: { "Content-Type": "application/json" },
    data: JSON.stringify(payload),
    authMode: "oauth",
  });
};

export const bankTransferDenyApi = async ({ order_id, uid }) => {
  return ajax({
    method: "POST",
    url: "/rest/bank-transfer/deny",
    headers: { "Content-Type": "application/json" },
    data: JSON.stringify({ order_id, uid }),
    authMode: "oauth",
  });
};
